import { Component, OnInit, OnDestroy, ViewChild, AfterViewInit } from '@angular/core';
import {
  IonApp,
  IonButton,
  IonContent,
  IonRouterOutlet,
  ModalController,
  NavController,
  Platform,
  ToastController,
} from '@ionic/angular/standalone';
import { Router, NavigationEnd, ActivatedRoute, NavigationStart } from '@angular/router';
import { firstValueFrom, Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { BottomNavComponent } from './components/bottom-nav/bottom-nav.component';
import { NavigationService } from './services/navigation.service';
import { NavigationHistoryService } from './services/navigation-history.service';
import { TransitionService } from './services/transition.service';
import { NavTab } from './models/nav-tab.interface';
import { RoutesApp } from './models/enums/routes-app.enum';
import { AuthService } from './services/auth.service';
import { UserService } from './services/user.service';
import { UserType } from './models/tab-config';
import { User } from './models/user.model';
import { StatusBar, Style } from '@capacitor/status-bar';
import { App } from '@capacitor/app';
import { Keyboard } from '@capacitor/keyboard';
import { ThemeService } from './services/theme.service';
import { ChatService } from './services/chat.service';
import { NotificationService } from './services/notification.service';
import { PushNotificationService } from './services/push-notification.service';
import { WorkoutReminderNotificationService } from './services/workout-reminder-notification.service';
import { ConnectivityService } from './services/connectivity.service';
import { PwaInstallService } from './services/pwa-install.service';
import { PwaDeviceService } from './services/pwa-device.service';
import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';
import { hasNativeSplash } from './utils/native-splash.util';
import { resolveProfileImageForDisplay } from './utils/media-url.util';
import { initAvatarImageLoading } from './utils/avatar-image-loading';
import { ReferralService } from './services/referral.service';
import { ReferralHighlightModalComponent } from './components/referral-highlight-modal/referral-highlight-modal.component';

/** Dá tempo de a primeira tela montar antes de o splash sair. */
const NATIVE_SPLASH_HIDE_DELAY_MS = 150;

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  imports: [IonApp, IonRouterOutlet, IonContent, IonButton, BottomNavComponent],
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild(IonRouterOutlet, { static: false }) routerOutlet!: IonRouterOutlet;
  
  navigationTabs: NavTab[] = [];
  activeTabId: string = '';
  showBottomNav: boolean = false;
  isOffline = false;
  isDesktopWebBlocked = false;
  /** URL da foto do perfil (usado na tab Perfil do aluno) */
  profileImageUrl: string | null = null;
  private subscription: Subscription = new Subscription();
  private backButtonSubscription: Subscription | null = null;
  private lastBackPressTime = 0;
  private static readonly BACK_EXIT_THRESHOLD_MS = 2000;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private navigationService: NavigationService,
    private navigationHistoryService: NavigationHistoryService,
    private authService: AuthService,
    private userService: UserService,
    private platform: Platform,
    private toastController: ToastController,
    private navController: NavController,
    private transitionService: TransitionService,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _themeService: ThemeService, // Injetado para garantir inicialização do tema escuro
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _chatService: ChatService, // Injetado para garantir inicialização antecipada e pré-carregamento de chats
    private notificationService: NotificationService,
    private pushNotificationService: PushNotificationService,
    private workoutReminderNotificationService: WorkoutReminderNotificationService,
    private connectivityService: ConnectivityService,
    private pwaInstallService: PwaInstallService,
    private pwaDeviceService: PwaDeviceService,
    private referralService: ReferralService,
    private modalController: ModalController
  ) {
    this.initializeApp();
    // Inicializar o serviço de histórico de navegação
    this.navigationHistoryService.init();
    // Skeleton de avatar enquanto a foto carrega (ver global.scss)
    initAvatarImageLoading();
  }

  ngAfterViewInit(): void {
    // Aguarda um tick para garantir que o router outlet está disponível
    setTimeout(() => {
      this.setupRouterTransition();
    }, 0);
  }

  /**
   * Retorna a função de animação para o router outlet
   * Esta função cria uma closure que sempre lê a direção atual do serviço
   */
  getRouterAnimation() {
    const service = this.transitionService;
    // Retorna uma função que será chamada pelo Ionic durante a navegação
    // A closure captura o serviço, permitindo ler a direção atual no momento da navegação
    return (baseEl: HTMLElement, opts: any) => {
      const currentDirection = service.getCurrentDirection();
      const animationFn = service.createTransitionAnimation(currentDirection);
      return animationFn(baseEl, opts);
    };
  }

  private setupRouterTransition(): void {
    // Configura a animação no router outlet se disponível
    if (this.routerOutlet) {
      (this.routerOutlet as any).animation = this.getRouterAnimation();
    }

    // Monitora mudanças de navegação para atualizar a direção
    this.subscription.add(
      this.router.events
        .pipe(filter(event => event instanceof NavigationStart))
        .subscribe((event: any) => {
          // Tenta obter a direção do state da navegação
          const navigationState = (event as any).navigation?.extras?.state;
          if (navigationState?.transitionDirection) {
            const direction = navigationState.transitionDirection;
            this.transitionService.setNextTransitionDirection(direction);
          }
        })
    );
  }

  /**
   * Inicializa configurações do app para dispositivos móveis
   * App exclusivo para mobile (iOS/Android)
   */
  private async initializeApp(): Promise<void> {
    await this.platform.ready();
    this.isDesktopWebBlocked = !this.pwaDeviceService.isWebAccessAllowed();

    if (!this.isDesktopWebBlocked) {
      this.scheduleInstallHint();
      this.scheduleReferralHighlight();
    }

    await this.initConnectivity();

    // App é exclusivo para mobile, mas ainda pode rodar no navegador durante desenvolvimento
    // Verificar se está em ambiente nativo (não no navegador)
    const isNative = this.platform.is('capacitor') && 
                    (this.platform.is('ios') || this.platform.is('android'));
    
    if (isNative) {
      // Altura do teclado: o app rola o conteúdo e o campo focado fica visível (ver global.scss)
      void Keyboard.addListener('keyboardWillShow', (info) => {
        document.documentElement.style.setProperty('--keyboard-height', `${info.keyboardHeight}px`);
        document.documentElement.classList.add('keyboard-open');
      });
      void Keyboard.addListener('keyboardWillHide', () => {
        document.documentElement.style.setProperty('--keyboard-height', '0px');
        document.documentElement.classList.remove('keyboard-open');
      });
      try {
        await StatusBar.setStyle({ style: Style.Dark });
        await StatusBar.setBackgroundColor({ color: '#000000' });
        await StatusBar.setOverlaysWebView({ overlay: false });
      } catch (error) {
        // Em desenvolvimento web, silenciar o erro (não é um problema)
        // Em produção mobile, logar o erro para debug
        if (!this.platform.is('mobileweb') && !this.platform.is('desktop')) {
          console.error('Erro ao configurar StatusBar/Keyboard:', error);
        }
      }
    }
  }

  /**
   * Dica de instalação do PWA só depois das telas de entrada (splash, seleção,
   * onboarding, login, cadastro), para não cobrir os cards e o formulário.
   */
  private scheduleInstallHint(): void {
    const entryRoute = /^\/(splash|user-type-selection|auth|register|student\/welcome|trainer\/register)?(\/|\?|#|$)/;
    let shown = false;
    const tryShow = (url: string) => {
      if (shown || entryRoute.test(url)) return;
      shown = true;
      void this.pwaInstallService.maybeShowInstallHint();
    };

    tryShow(this.router.url);
    this.subscription.add(
      this.router.events
        .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
        .subscribe((event) => tryShow(event.urlAfterRedirects))
    );
  }

  /**
   * Tela do brinde da campanha de indicação: uma vez por campanha, já dentro do app
   * (fora de cadastro/onboarding), para quem ainda não participa.
   */
  private scheduleReferralHighlight(): void {
    const appArea = /^\/(student|trainer)\//;
    const skipRoute = /^\/(student\/(onboarding|welcome)|trainer\/(basic-info|complete-profile|register))(\/|\?|#|$)/;
    let checking = false;
    let doneThisSession = false;

    const tryShow = async (url: string) => {
      if (checking || doneThisSession || !appArea.test(url) || skipRoute.test(url)) return;
      const user = this.authService.getCurrentUser() as (User & { _id?: string }) | null;
      const userId = user?.id || user?._id;
      if (!userId || !this.authService.isAuthenticated()) return;

      checking = true;
      try {
        const status = await firstValueFrom(this.referralService.getMyStatus());
        doneThisSession = true;
        const campaignId = status.campaign.campaignId;
        if (!status.showHighlight || this.referralService.wasHighlightSeen(userId, campaignId)) return;
        // Guardas podem ter redirecionado para o onboarding enquanto a campanha carregava
        const currentUrl = this.router.url;
        if (!appArea.test(currentUrl) || skipRoute.test(currentUrl)) {
          doneThisSession = false;
          return;
        }

        this.referralService.markHighlightSeen(userId, campaignId);
        const modal = await this.modalController.create({
          component: ReferralHighlightModalComponent,
          componentProps: {
            prizeName: status.campaign.prizeName,
            prizeDescription: status.campaign.prizeDescription,
            prizeImageUrl: status.campaign.prizeImageUrl,
            goal: status.campaign.goal
          }
        });
        await modal.present();
      } catch {
        // Sem campanha ou sem rede: tenta de novo na próxima navegação
      } finally {
        checking = false;
      }
    };

    void tryShow(this.router.url);
    this.subscription.add(
      this.router.events
        .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
        .subscribe((event) => void tryShow(event.urlAfterRedirects))
    );
  }

  private async initConnectivity(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    await this.connectivityService.init();
    this.subscription.add(
      this.connectivityService.online$.subscribe((online) => {
        const wasOffline = this.isOffline;
        this.isOffline = !online;
        if (wasOffline && online) {
          window.location.reload();
        }
      })
    );
  }

  async retryConnection(): Promise<void> {
    const online = await this.connectivityService.refreshStatus();
    if (online) {
      window.location.reload();
    }
  }

  /** Mantém o splash nativo até a primeira tela real; a rota /splash só decide para onde ir. */
  private hideNativeSplashOnFirstScreen(): void {
    if (!hasNativeSplash()) return;
    this.subscription.add(
      this.router.events
        .pipe(
          filter((event): event is NavigationEnd => event instanceof NavigationEnd),
          filter((event) => !event.urlAfterRedirects.startsWith(`/${RoutesApp.SPLASH}`)),
          take(1)
        )
        .subscribe(() => {
          // Sem requestAnimationFrame: a WebView não desenha enquanto o splash segura a tela
          setTimeout(() => void SplashScreen.hide(), NATIVE_SPLASH_HIDE_DELAY_MS);
        })
    );
  }

  ngOnInit(): void {
    this.hideNativeSplashOnFirstScreen();

    // Subscribe to current user changes to update navigation tabs
    this.subscription.add(
      this.authService.currentUser$.subscribe((user) => {
        this.updateNavigationForUser();
        const isNative = this.platform.is('capacitor') && (this.platform.is('ios') || this.platform.is('android'));
        if (user && isNative) {
          this.pushNotificationService.init().then(() => this.pushNotificationService.handleLaunchFromNotification());
        }
      })
    );
    
    // Subscribe to route changes to update active tab
    this.subscription.add(
      this.router.events
        .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
        .subscribe(event => {
          this.updateActiveTab(event.url);
          // Atualizar foto da tab Perfil quando o usuário navega para o perfil (ex.: após editar foto)
          const cleanUrl = event.url.split(/[?#]/)[0];
          if (this.authService.isStudent() && (cleanUrl === '/student/user-profile' || cleanUrl === '/student/edit-profile')) {
            this.loadStudentProfileImage();
          }
        })
    );

    // Subscribe to active tab changes
    this.subscription.add(
      this.navigationService.getActiveTabObservable().subscribe(tabId => {
        this.activeTabId = tabId;
      })
    );

    // Set initial navigation and active tab
    this.updateNavigationForUser();
    this.updateActiveTab(this.router.url);

    this.platform.ready().then(() => {
      this.setupBackButtonHandler();
      this.workoutReminderNotificationService.init();
      const isNative =
        this.platform.is('capacitor') &&
        (this.platform.is('ios') || this.platform.is('android'));
      if (isNative && this.authService.isAuthenticated()) {
        void this.pushNotificationService.init().then(() =>
          this.pushNotificationService.handleLaunchFromNotification()
        );
      }
    });
  }

  ngOnDestroy(): void {
    if (this.backButtonSubscription) {
      this.backButtonSubscription.unsubscribe();
      this.backButtonSubscription = null;
    }
    this.workoutReminderNotificationService.destroy();
    this.subscription.unsubscribe();
  }

  onTabSelected(tab: NavTab): void {
    this.navigationService.setActiveTab(tab.id);
  }

  /**
   * Atualiza as tabs de navegação baseado no tipo de usuário atual
   */
  private updateNavigationForUser(): void {
    const currentUser = this.authService.getCurrentUser();
    
    if (!currentUser) {
      this.navigationService.setUserType(UserType.GUEST);
      this.navigationTabs = [];
      this.profileImageUrl = null;
      this.notificationService.refreshUnreadCount();
      // Limpar histórico de navegação quando usuário faz logout
      this.navigationHistoryService.clearHistory();
      this.pushNotificationService.unregister();
      return;
    }

    // Determinar o tipo de usuário e configurar as tabs apropriadas
    if (this.authService.isStudent()) {
      this.navigationService.setUserType(UserType.STUDENT);
      this.navigationTabs = this.navigationService.getStudentTabs();
      this.loadStudentProfileImage();
      this.notificationService.refreshUnreadCount();
    } else if (this.authService.isTrainer()) {
      this.navigationService.setUserType(UserType.TRAINER);
      this.navigationTabs = this.navigationService.getTrainerTabs();
      this.profileImageUrl = null;
      this.notificationService.refreshUnreadCount();
    } else {
      this.navigationService.setUserType(UserType.GUEST);
      this.navigationTabs = [];
      this.profileImageUrl = null;
    }
  }

  /**
   * Carrega a foto do perfil do aluno para exibir na tab bar
   */
  private loadStudentProfileImage(): void {
    this.userService.getCurrentUserProfile().subscribe({
      next: (res) => {
        const raw = res?.data?.profileImage?.trim();
        this.profileImageUrl = raw ? resolveProfileImageForDisplay(raw) : null;
      },
      error: () => {
        this.profileImageUrl = null;
      }
    });
  }

  private updateActiveTab(url: string): void {
    // Remove query params e hash da URL para comparação
    const cleanUrl = url.split(/[?#]/)[0];
    
    // Encontra a tab ativa fazendo correspondência mais precisa
    // Primeiro tenta correspondência exata, depois verifica se a URL começa com a rota da tab
    const activeTab = this.navigationTabs.find(tab => {
      const tabRoute = `/${tab.route}`;
      // Correspondência exata ou a URL começa com a rota da tab seguida de / ou fim da string
      return cleanUrl === tabRoute || cleanUrl.startsWith(tabRoute + '/') || cleanUrl === tabRoute;
    });
    
    if (activeTab) {
      this.activeTabId = activeTab.id;
      this.navigationService.setActiveTab(activeTab.id);
    }

    // Verificar se deve mostrar o bottom-nav
    this.showBottomNav = this.shouldShowBottomNav(url);
  }

  private shouldShowBottomNav(url: string): boolean {
    // Pega só o path antes de qualquer query (? ou #)
    const cleanUrl = url.split(/[?#]/)[0];
    
    // Splash screen NUNCA deve mostrar bottom-nav
    if (cleanUrl === '/splash' || cleanUrl === 'splash' || cleanUrl === '/' || cleanUrl === '') {
      return false;
    }
    
    // Verificar se a rota atual tem data.hideTabs = true
    const routeData = this.getRouteData(this.activatedRoute);
    if (routeData?.['hideTabs'] === true) {
      return false;
    }
    
    // Rotas públicas que não devem mostrar o bottom-nav
    const publicRoutesWithoutBottomNav = [
      RoutesApp.SPLASH,
      `/${RoutesApp.SPLASH}`, // Com barra inicial
      RoutesApp.AUTH,
      RoutesApp.REGISTER,
      RoutesApp.USER_TYPE_SELECTION,
      `/${RoutesApp.USER_TYPE_SELECTION}`, // Com barra inicial
      RoutesApp.STUDENT_LOGIN,
      RoutesApp.TRAINER_LOGIN,
      'auth/login',
      'auth/reset-password',
      'contact',
      'terms-of-use'
    ];
    
    // Rotas student que não devem mostrar bottom-nav
    const studentRoutesWithoutBottomNav = [
      'student/welcome',
      'student/workout-details',
      'student/workout-browser',
      'student/trainer-workouts',
      'student/trainer-profile',
      'student/student-profile',
      'student/today-workout',
      'student/workout-session',
      'student/workout-builder',
      'student/workout-digitize',
      'student/workout-celebration',
      'student/onboarding',
      'student/goal-workouts',
      'student/edit-profile',
      'student/settings',
      'student/feature-preferences',
      'student/nutrition',
      'student/nutrition/meal-plans',
      'student/nutrition/meal-plan-check',
      'student/nutrition/meal-plan/view',
      'student/feed/create'
    ];
    
    // Rotas trainer que não devem mostrar bottom-nav
    const trainerRoutesWithoutBottomNav = [
      'trainer/create-workout',
      'trainer/workout-builder',
      'trainer/complete-profile',
      'trainer/basic-info',
      'trainer/register',
      'trainer/manage-exercises/form',
      'trainer/student-profile',
      'trainer/students/', // ficha do aluno (a lista /trainer/students mantém a barra)
      'trainer/edit-profile',
      'trainer/feature-preferences',
      'trainer/reviews',
      'trainer/nutrition/meal-plan/create',
      'trainer/nutrition/meal-plan/edit',
      'trainer/nutrition/templates',
      'trainer/feed/create'
    ];
    
    // Rotas compartilhadas que não devem mostrar bottom-nav
    const sharedRoutesWithoutBottomNav = [
      'chat', // Rota de chat individual (não a lista chat-list)
      'notifications' // Tela de notificações (aberta pelo ícone no perfil ou por link)
    ];
  
    // Verificar rotas públicas
    if (publicRoutesWithoutBottomNav.some(route => cleanUrl.includes(route))) {
      return false;
    }
    
    // Verificar rotas student
    if (this.authService.isStudent() && studentRoutesWithoutBottomNav.some(r => cleanUrl.includes(r))) {
      return false;
    }
    
    // Verificar rotas trainer
    if (this.authService.isTrainer() && trainerRoutesWithoutBottomNav.some(r => cleanUrl.includes(r))) {
      return false;
    }
    
    // Verificar rotas compartilhadas
    // Para chat, ocultar apenas na conversa individual, não na lista
    if (cleanUrl === '/chat' || cleanUrl.startsWith('/chat?')) {
      return false;
    }
    
    if (sharedRoutesWithoutBottomNav.some(route => cleanUrl.includes(route) && cleanUrl !== '/chat-list')) {
      return false;
    }
    
    // Se o usuário não está autenticado, não mostrar bottom-nav
    if (!this.authService.isAuthenticated()) {
      return false;
    }
    
    return true;
  }

  /**
   * Obtém os dados da rota ativa, percorrendo a árvore de rotas
   */
  private getRouteData(route: ActivatedRoute): any {
    let child = route;
    while (child.firstChild) {
      child = child.firstChild;
    }
    return child.snapshot.data;
  }

  /**
   * Rotas do fluxo de auth: não interceptar back; deixar handlers das páginas ou padrão.
   */
  private isAuthRoute(url: string): boolean {
    const clean = url.split(/[?#]/)[0];
    const authPaths = [
      '/splash',
      `/${RoutesApp.USER_TYPE_SELECTION}`,
      '/student/welcome',
      `/${RoutesApp.AUTH}`,
      '/auth/login',
      '/auth/reset-password',
      `/${RoutesApp.REGISTER}`,
      '/trainer/register'
    ];
    return authPaths.some(p => clean === p || clean.startsWith(p + '/'));
  }

  /**
   * Rotas raiz pós-login (tabs + primeiro acesso): back = sair do app.
   */
  private isRootRoute(url: string): boolean {
    const clean = url.split(/[?#]/)[0];
    const roots = [
      '/student',
      '/student/trainers',
      '/student/feed',
      '/student/my-workouts',
      '/chat-list',
      '/notifications',
      '/student/user-profile',
      '/trainer/dashboard',
      '/trainer/students',
      '/trainer/feed',
      '/trainer/my-workouts',
      '/trainer/settings',
      '/trainer/manage-exercises',
      '/student/onboarding',
      '/trainer/basic-info',
      '/trainer/complete-profile'
    ];
    return roots.some(r => clean === r);
  }

  private setupBackButtonHandler(): void {
    if (!this.platform.is('android') && !this.platform.is('capacitor')) {
      return;
    }
    this.backButtonSubscription = this.platform.backButton.subscribeWithPriority(5, (processNextHandler) => {
      const url = this.router.url;
      if (!this.authService.isAuthenticated()) {
        processNextHandler();
        return;
      }
      // Logado: telas de login/cadastro não devem ser acessadas pelo voltar nativo
      if (this.isAuthRoute(url)) {
        void this.navController.navigateRoot(['/student']);
        return;
      }
      if (!this.isRootRoute(url)) {
        processNextHandler();
        return;
      }
      const now = Date.now();
      if (now - this.lastBackPressTime < AppComponent.BACK_EXIT_THRESHOLD_MS) {
        this.lastBackPressTime = 0;
        App.exitApp().catch(() => {});
        return;
      }
      this.lastBackPressTime = now;
      this.toastController.create({
        message: 'Pressione novamente para sair',
        duration: 2000,
        position: 'bottom'
      }).then(t => t.present());
    });
  }
}
